<template>
    <div class="product-tab-content">

        <p class="detail-header" v-if="product.discount_cashback">
            <strong>Extra Keuze.nl cashback</strong>
        </p>

        <p v-if="product.discount_cashback">
            Bij dit abonnement krijg je
            <strong>{{ toCurrencyShortEur(product.discount_cashback) }} extra cashback van Keuze.nl.</strong>
        </p>

        <p v-if="product.discount_cashback">
            Stap over via Keuze.nl, meld je meteen aan op de {{ product.provider_name }} website en vraag daarna je
            cashback
            aan.
            Lees op <a href="/cashback" target="_blank">keuze.nl/cashback</a> de voorwaarden en de instructies.
        </p>

        <hr v-if="product.discount_cashback" />

        <pricing-table>
            <pricing-table-header>
                {{ product.provider_name }} {{ product.base_name }} abonnement
            </pricing-table-header>

            <pricing-table-row>
                <template #description>Contractduur</template>
                <template #value>
                    {{ product.contract_duration }}
                    <template v-if="product.contract_duration === 1">maand</template>
                    <template v-else>maanden</template>
                </template>
            </pricing-table-row>

            <pricing-table-row>
                <template #description>Abonnementsprijs</template>
                <template #value>{{ toCurrencyEur(product.total_price) }} per maand</template>
            </pricing-table-row>

            <pricing-table-row v-if="product.base_price_initial">
                <template #description>Aansluitkosten</template>
                <template #value>{{ toCurrencyEur(product.base_price_initial) }}</template>
            </pricing-table-row>

            <pricing-table-header>
                Kortingen &amp; acties
            </pricing-table-header>
            <template v-if="hasMultipleDiscountDurations">
                <pricing-table-row v-if="product.discount_duration > 0">
                    <template #description>
                        Korting internet
                    </template>
                    <template #value>
                        {{ product.discount_duration }}
                        <template v-if="product.discount_duration === 1">maand</template>
                        <template v-else>maanden</template>
                        ⨉ - {{ toCurrencyEur(totalDiscountMonthly) }}
                    </template>
                </pricing-table-row>
                <pricing-table-row v-if="product.tv.included && product.tv.discount_duration > 0">
                    <template #description>
                        Korting TV
                    </template>
                    <template #value>
                        {{ product.tv.discount_duration }}
                        <template v-if="product.tv.discount_duration === 1">maand</template>
                        <template v-else>maanden</template>
                        ⨉ - {{ toCurrencyEur((product.tv.monthly_price - product.tv.discount_price)) }}
                    </template>
                </pricing-table-row>
                <pricing-table-row v-if="product.phone.included && product.phone.discount_duration > 0">
                    <template #description>
                        Korting Bellen
                    </template>
                    <template #value>
                        {{ product.phone.discount_duration }}
                        <template v-if="product.phone.discount_duration === 1">maand</template>
                        <template v-else>maanden</template>
                        ⨉ - {{ toCurrencyEur((product.phone.monthly_price - product.phone.discount_price)) }}
                    </template>
                </pricing-table-row>
            </template>
            <template v-else-if="hasDiscount">
                <pricing-table-row v-if="hasDiscount">
                    <template #description>
                        Kortingsactie
                    </template>
                    <template #value>
                        {{ visibleDiscountDuration }}
                        <template v-if="product.discount_duration === 1">maand</template>
                        <template v-else>maanden</template>
                        ⨉
                        - {{ toCurrencyEur(totalDiscountMonthly) }}
                    </template>
                </pricing-table-row>
            </template>

            <pricing-table-row v-if="product.combi_discount > 0">
                <template #description>
                    Totaal combivoordeel
                </template>
                <template #value>
                    - {{ toCurrencyEur(product.combi_discount) }}
                </template>
            </pricing-table-row>

            <pricing-table-row v-if="product.discount_amount_initial > 0">
                <template #description>
                    <template v-if="product.discount_amount_initial === product.base_price_initial">
                        Geen aansluitkosten
                    </template>
                    <template v-else>
                        Eenmalige korting
                    </template>
                </template>
                <template #value>
                    - {{ toCurrencyEur(product.discount_amount_initial) }}
                </template>
            </pricing-table-row>

            <pricing-table-row v-if="product.discount_cashback > 0">
                <template #description>
                    Cashback
                </template>
                <template #value>
                    - {{ toCurrencyEur(product.discount_cashback) }}
                </template>
            </pricing-table-row>

            <template v-if="product.additional.enabled && product.additional.enabled.length > 0">

                <pricing-table-header>
                    Abonnement extra's
                </pricing-table-header>

                <price-calculation :product="additionalProduct" :contract-duration="product.contract_duration"
                    v-for="additionalProduct in product.additional.enabled" :icon="additionalProduct.category"
                    :key="additionalProduct.id">
                </price-calculation>

            </template>

            <template v-if="product.tv.enabled && product.tv.enabled.length > 0">

                <pricing-table-header>
                    TV-extra's
                </pricing-table-header>

                <price-calculation :product="tvPackage" :contract-duration="product.contract_duration"
                    v-for="tvPackage in product.tv.enabled" icon="tv" :key="tvPackage.id">
                </price-calculation>

            </template>

            <pricing-table-header>
                Totaal
            </pricing-table-header>

            <pricing-table-row v-if="product.total_price_contract !== product.compare_price">
                <template #description>
                    {{ comparisonStore.filters.contractDuration > 12 ? 'Gemiddelde jaarprijs' : 'Prijs 1e jaar' }}
                </template>
                <template #value>
                    {{ toCurrencyEur(product.compare_price) }}
                </template>
            </pricing-table-row>

            <pricing-table-row v-if="product.contract_duration > 1">
                <template #description>
                    Totaalprijs {{ product.contract_duration }} maanden
                </template>
                <template #value>
                    {{ toCurrencyEur(product.total_price_contract) }}
                </template>
            </pricing-table-row>

        </pricing-table>
    </div>

</template>

<script setup>
import PricingTable from 'vue-elements/product-info/PricingTable.vue';
import PricingTableHeader from 'vue-elements/product-info/PricingTableHeader.vue';
import PricingTableRow from 'vue-elements/product-info/PricingTableRow.vue';
import PriceCalculation from 'vue-elements/product-info/PriceCalculation.vue';
import { computed } from 'vue';
import { useInternetComparisonStore } from '../../stores/internetComparison';

const comparisonStore = useInternetComparisonStore();
const props = defineProps({
    product: Object,
    startTab: String,
});
const hasDiscount = computed(() => {

    const hasInternetDiscount = props.product.discount_duration > 0;

    const hasTvDiscount = props.product.tv.included
        && props.product.tv.discount_price != null
        && props.product.tv.discount_price < props.product.tv.monthly_price;

    const hasPhoneDiscount = props.product.phone.included
        && props.product.phone.discount_price != null
        && props.product.phone.discount_price < props.product.phone.monthly_price;

    return hasInternetDiscount || hasTvDiscount || hasPhoneDiscount;
});


const hasMultipleDiscountDurations = computed(() => {
    const internetDiscount = props.product.discount_duration || 0;
    const tvDiscount = props.product.tv.included ? (props.product.tv.discount_duration || 0) : 0;
    const phoneDiscount = props.product.phone.included ? (props.product.phone.discount_duration || 0) : 0;

    return (
        (internetDiscount > 0 && tvDiscount > 0 && internetDiscount !== tvDiscount) ||
        (internetDiscount > 0 && phoneDiscount > 0 && internetDiscount !== phoneDiscount) ||
        (tvDiscount > 0 && phoneDiscount > 0 && tvDiscount !== phoneDiscount)
    );
});

const totalDiscountMonthly = computed(() => {
    let discount = props.product.discount_amount_monthly || 0;

    if (props.product.tv.included) {
        discount += props.product.tv.discount_amount_monthly || 0;
    }

    if (props.product.phone.included) {
        discount += props.product.phone.discount_amount_monthly || 0;
    }
    return discount;
});

const visibleDiscountDuration = computed(() => {
    if (props.product.discount_duration > 0) {
        return props.product.discount_duration;
    } else if (props.product.tv.included && props.product.tv.discount_duration > 0) {
        return props.product.tv.discount_duration;
    } else if (props.product.phone.included && props.product.phone.discount_duration > 0) {
        return props.product.phone.discount_duration;
    }
    return 0;
});

</script>